import _ from 'lodash'
import eventBus from './eventbus'
import sdk from './sdk'

export function getPageInfo(tizraId, pageNum) {
  return api('reader/page-info', {'tizra-id': tizraId, 'page-num': pageNum})
    .then(sdk.camel)
    .then(r => ({
      pageNum,
      ...r,
    }))
}

export function getPubInfo(tizraId) {
  return api('reader/pub-info', {'tizra-id': tizraId})
    .then(sdk.camel)
    .then(r => ({
      tizraId,
      urlId: tizraId,
      detailUrl: `/${tizraId}/`,
      firstPage: 1,
      lastPage: r.numPages,
      ...r,
    }))
}

export function getToc(tizraId) {
  return api('reader/toc', {'tizra-id': tizraId})
}

export function getLogicalPages(tizraId) {
  return api('logical-pages', {'tizra-id': tizraId})
}

export function getSearchResults(tizraId, terms) {
  const query = `full-text:PdfPage:pageFullText:${terms}`
  return api('search', {
    any: query,
    'meta-type': 'PdfPage',
    'filter-title-group': tizraId,
    'snippet-prop': 'pageFullText',
    'snippet-query': query,
    page: 20,
    start: 0,
  }).then(r => {
    // Convert to JS-friendly response
    return {
      ...r,
      results: r.result.map(rr => ({
        logicalPageNum: rr['logical-page-number'],
        pageNum: rr['page-number'],
        snippetHtml: rr['snippet'],
      })),
    }
  })
}

export function getOverlappingExcerpts(
  tizraId,
  pageNums,
  fields = [],
  props = [],
) {
  return api('overlapping-excerpts', {
    'tizra-id': tizraId,
    'page-number': pageNums,
    fields: _.union(fields, ['pages']),
    props,
  }).then(r =>
    _.mapValues(r, excerpts =>
      excerpts.sort(
        // prettier-ignore
        (a, b) => (a.pages[0] < b.pages[0] ? -1 :  // earlier
                           a.pages[0] > b.pages[0] ? 1 :
                           a.pages[1] > b.pages[1] ? -1 :  // larger
                           a.pages[1] < b.pages[1] ? 1 : 0),
      ),
    ),
  )
}

export function getOverlappingTitles(tizraId, pageNums) {
  return getOverlappingExcerpts(
    tizraId,
    pageNums,
    ['parsed-props'],
    ['Title'],
  ).then(r =>
    _.mapValues(r, excerpts => excerpts.map(e => e['parsed-props']['Title'])),
  )
}

export function getHighlights(tizraId, pageNum, query) {
  return api(
    'highlighted-value',
    {
      'tizra-id': tizraId,
      'page-num': pageNum,
      query,
    },
    {
      json: false,
    },
  )
}

export function getAttachments(tizraId) {
  return api('query/attachments', {'tizra-id': tizraId}).then(r => r.result)
}

export function getAnchorPage(tizraId, anchor) {
  return api('search', {
    any: `anchorId:${anchor}`,
    'meta-type': 'PdfPage',
    'filter-title-group': tizraId,
    page: 1,
    start: 0,
  }).then(r => r.result[0]?.['page-number'])
}

export function api(...args) {
  return sdk.api(...args).catch(e => {
    if (_.get(e, 'response.status') === 403) {
      // Access denied, handled by redirecting.
      eventBus.$emit('denied')
    } else {
      eventBus.$emit('error', e)
    }
    throw e // should be caught by caller
  })
}

export function getExtractedText(url) {
  return sdk.pseudoApi(url).then(html => {
    // Make sure we got the fragment we expect, and not a full
    // Tizra page.
    return /^<div class=.t-extracted-page/.test(`${html}`.trim()) ? html : ''
  })
}

export function getPageXml(url) {
  return sdk.pseudoApi(url)
}

export function getReaderAttachments(url) {
  return sdk.pseudoApi(url).then(html => {
    // Render the HTML to a div that will not appear on the page.
    // This does not execute <script> tags in the HTML.
    const x = document.createElement('div')
    x.innerHTML = html

    // Traverse the rendered HTML to build up a data structure.
    const data = {result: []}
    let current = {}
    Array.from(x.children).forEach(child => {
      if (child.classList.contains('t-download-header')) {
        data.heading = child.innerText.trim()
      } else if (child.classList.contains('t-attachment-header')) {
        current.heading = child.innerText.trim()
      } else if (
        current.heading &&
        child.tagName === 'UL' &&
        child.classList.contains('t-attachment-list')
      ) {
        current.name = child.dataset.name
        current.attachments = Array.from(child.children)
          .filter(li => li.children.length && li.children[0].tagName === 'A')
          .map(li => {
            const a = li.children[0]
            const span = a.getElementsByClassName('t-attachment-name')[0]
            const name = span && span.innerText.trim()
            if (name) {
              return {
                name,
                ..._.pick(a, ['href', 'rel', 'target']),
              }
            }
          })
          .filter(Boolean)
        if (current.attachments.length) {
          data.result.push(current)
        }
        current = {}
      }
    })

    return data
  })
}

export default {
  getAnchorPage,
  getAttachments,
  getExtractedText,
  getHighlights,
  getLogicalPages,
  getOverlappingExcerpts,
  getOverlappingTitles,
  getReaderAttachments,
  getPageInfo,
  getPageXml,
  getPubInfo,
  getSearchResults,
  getToc,
}
